import type { Dispatch, SetStateAction } from 'react';
import { Alert, Button, FormGroup, FormParagraph, FormSection } from '@ksyos/design-system';
import { orderBy } from 'lodash';
import { useEffect, useState } from 'react';
import type { IPreferredServiceOrganizationConsumer } from '../../../interfaces/preferredServiceOrganizationConsumer';
import type { IServiceOrganizationConsumer } from '../../../interfaces/serviceOrganizationConsumer';
import type { IPersonalSettingsCarePathServiceProviderSettingFormSectionProps } from './PersonalSettingsCarePathServiceProviderSetting';
import { ServiceType } from '../../../../../common/enums/serviceType';
import { translateKey } from '../../../../translate';
import { SortDirection } from '../../../../utils/sortDirection';
import { InputTriState } from '../../../elements/common';
import { ConfirmationModal } from '../../../elements/modals/ConfirmationModal';
import { ActionBarShowAndRemoveSelection } from '../../../userDataManagement/ActionBarShowAndRemoveSelection';
import { PersonalSettingsPreferenceListExaminationServiceConsumerSearchNationWideModal } from '../preferenceLists/PreferenceListExamination/Modals/PersonalSettingsPreferenceListExaminationServiceConsumerSearchNationWideModal';
import { PersonalSettingsPreferenceListExaminationConsumersTable } from '../preferenceLists/PreferenceListExamination/PreferenceListExaminationTables/PersonalSettingsPreferenceListExaminationConsumersTable';

interface ISelectConsumerSettingsProps extends IPersonalSettingsCarePathServiceProviderSettingFormSectionProps {
    employmentId: string;
    noPreferredServiceConsumerSelected: boolean;
    originalPreferredOrganizationConsumers: IPreferredServiceOrganizationConsumer[];
    preferredOrganizationConsumers: IServiceOrganizationConsumer[];
    setNoPreferredServiceConsumerSelected: Dispatch<SetStateAction<boolean>>;
    setPreferredOrganizationConsumers: Dispatch<SetStateAction<IServiceOrganizationConsumer[]>>;
}

export function SelectConsumerSettings(props: ISelectConsumerSettingsProps) {
    const [showAddOrganizationNationWideModal, setShowAddOrganizationNationWideModal] = useState(false);
    const [selectedConsumerOrganizationIds, setSelectedConsumerOrganizationIds] = useState<string[]>([]);
    const [removedOriginalConsumerOrganizations, setRemovedOriginalConsumerOrganizations] = useState<
        IPreferredServiceOrganizationConsumer[]
    >([]);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [sortKey, setSortKey] = useState('organization.name');
    const [sortDirection, setSortDirection] = useState(SortDirection.asc);

    useEffect(() => {
        sortPreferredConsumers();
    }, [sortKey, sortDirection]);

    useEffect(() => {
        if (
            props.mode === 'read' &&
            (selectedConsumerOrganizationIds.length > 0 || removedOriginalConsumerOrganizations.length > 0)
        ) {
            setSelectedConsumerOrganizationIds([]);
            setRemovedOriginalConsumerOrganizations([]);
        }
    }, [props.mode]);

    if (props.service.serviceType !== ServiceType.examination || !props.isRequestCreator) {
        return null;
    }

    return (
        <FormSection title={translateKey('addCarePathSettingsHeaders.selectConsumer')}>
            <FormGroup label={translateKey('addCarePathSettingsLabelsServiceProvider.isServiceConsumerSelector')}>
                {props.mode === 'read' ? (
                    <FormParagraph>
                        {props.isServiceConsumerSelector ? translateKey('common.yes') : translateKey('common.no')}
                    </FormParagraph>
                ) : (
                    <InputTriState
                        fieldKey="isServiceConsumerSelector"
                        notNullable
                    />
                )}
            </FormGroup>

            {props.isServiceConsumerSelector && (
                <>
                    {props.mode === 'edit' && (
                        <>
                            {props.noPreferredServiceConsumerSelected ? (
                                <Alert appearance="warning">
                                    {translateKey(
                                        'addCarePathSettingsLabelsServiceProvider.addOnePreferredServiceConsumerOrganization'
                                    )}
                                </Alert>
                            ) : (
                                <Alert appearance="info">
                                    {translateKey(
                                        'addCarePathSettingsLabelsServiceProvider.addOnePreferredServiceConsumerOrganization'
                                    )}
                                </Alert>
                            )}

                            {props.preferredOrganizationConsumers.length > 0 && (
                                <PersonalSettingsPreferenceListExaminationConsumersTable
                                    onChange={async (selected) => setSelectedConsumerOrganizationIds(selected)}
                                    onSort={handleSort}
                                    selectedServiceOrganizationConsumersOrganizationIds={
                                        selectedConsumerOrganizationIds
                                    }
                                    serviceOrganizationConsumers={props.preferredOrganizationConsumers}
                                    sortDirection={sortDirection}
                                    sortKey={sortKey}
                                />
                            )}
                            <Button
                                iconLeft="add"
                                onClick={() => setShowAddOrganizationNationWideModal(true)}
                                style={{ marginBottom: '16px' }}
                                variant="link"
                            >
                                {translateKey('organization.add')}
                            </Button>

                            <PersonalSettingsPreferenceListExaminationServiceConsumerSearchNationWideModal
                                consumerOrganizationIdsToAdd={removedOriginalConsumerOrganizations.map(
                                    (preferredOrganizationConsumer) => preferredOrganizationConsumer.organization.id
                                )}
                                consumerOrganizationIdsToExclude={props.preferredOrganizationConsumers.map(
                                    (preferredOrganizationConsumer) => preferredOrganizationConsumer.organization.id
                                )}
                                isOpen={showAddOrganizationNationWideModal}
                                onAddOrganizations={handleAddOrganizations}
                                onClose={async () => setShowAddOrganizationNationWideModal(false)}
                                providerId={props.id}
                                serviceId={props.service.id}
                            />

                            <ActionBarShowAndRemoveSelection
                                handleClose={handleClose}
                                handleDelete={handleDeleteSelectedPreferredOrganizations}
                                isOpen={selectedConsumerOrganizationIds.length > 0}
                                selectedIds={selectedConsumerOrganizationIds}
                            />

                            <ConfirmationModal
                                cancelText={translateKey(
                                    'personalSettings.preferenceList.deletePreferenceListCancelMessage'
                                )}
                                confirmText={translateKey(
                                    'personalSettings.preferenceList.deletePreferenceListConfirmMessage'
                                )}
                                handleNo={handleModalNo}
                                handleYes={handleModalYes}
                                isOpen={showConfirmationModal}
                                question={
                                    <>
                                        {translateKey(
                                            'personalSettings.preferenceList.deletePreferenceListMessageThree'
                                        )}
                                        <strong>
                                            {translateKey(
                                                'personalSettings.preferenceList.deletePreferenceListMessageFour'
                                            )}
                                        </strong>
                                    </>
                                }
                                title={translateKey('personalSettings.preferenceList.deletePreferenceListTitle')}
                            />
                        </>
                    )}
                    <FormGroup
                        label={translateKey(
                            'addCarePathSettingsLabelsServiceProvider.canSelectCareProviderAsServiceConsumer'
                        )}
                    >
                        {props.mode === 'read' ? (
                            <FormParagraph>
                                {props.canSelectCareProviderAsServiceConsumer
                                    ? translateKey('common.yes')
                                    : translateKey('common.no')}
                            </FormParagraph>
                        ) : (
                            <InputTriState
                                fieldKey="canSelectCareProviderAsServiceConsumer"
                                notNullable
                            />
                        )}
                    </FormGroup>
                </>
            )}
        </FormSection>
    );

    async function addPreferredServiceOrganizationConsumersForServiceProvider(
        serviceOrganizationConsumers: IServiceOrganizationConsumer[]
    ) {
        const removedOrganizations = removedOriginalConsumerOrganizations.filter(
            (removedOrganization) =>
                !serviceOrganizationConsumers.find(
                    (serviceOrganizationConsumer) =>
                        serviceOrganizationConsumer.organization.id === removedOrganization.organization.id
                )
        );

        setRemovedOriginalConsumerOrganizations(removedOrganizations);
        props.setPreferredOrganizationConsumers([
            ...props.preferredOrganizationConsumers,
            ...serviceOrganizationConsumers,
        ]);
    }

    async function handleAddOrganizations(
        serviceOrganizationConsumerOrganizationIds: string[],
        selectedServiceOrganizationconsumerOrganizations?: IServiceOrganizationConsumer[]
    ) {
        setShowAddOrganizationNationWideModal(false);
        if (selectedServiceOrganizationconsumerOrganizations) {
            await addPreferredServiceOrganizationConsumersForServiceProvider(
                selectedServiceOrganizationconsumerOrganizations
            );
            props.setNoPreferredServiceConsumerSelected(false);
        }
    }

    async function handleSort(newSortKey: string, newSortDirection: SortDirection) {
        setSortKey(newSortKey);
        setSortDirection(newSortDirection);
    }

    function sortPreferredConsumers() {
        props.setPreferredOrganizationConsumers((current) => orderBy(current, sortKey, sortDirection));
    }

    async function handleModalYes() {
        setShowConfirmationModal(false);
        await deleteSelectedPreferredOrganizations();
    }

    function handleModalNo() {
        setShowConfirmationModal(false);
    }

    async function handleDeleteSelectedPreferredOrganizations() {
        if (props.preferredOrganizationConsumers.length === selectedConsumerOrganizationIds.length) {
            setShowConfirmationModal(true);
        } else {
            deleteSelectedPreferredOrganizations();
        }
    }

    async function deleteSelectedPreferredOrganizations() {
        const filteredPreferredOrganizationConsumers = props.preferredOrganizationConsumers.filter(
            (preferredOrganization) => !selectedConsumerOrganizationIds.includes(preferredOrganization.organization.id)
        );
        setRemovedOriginalConsumerOrganizations(
            props.originalPreferredOrganizationConsumers.filter((preferredOrganization) =>
                selectedConsumerOrganizationIds.includes(preferredOrganization.organization.id)
            )
        );
        props.setPreferredOrganizationConsumers(filteredPreferredOrganizationConsumers);
        setSelectedConsumerOrganizationIds([]);
    }

    async function handleClose() {
        setSelectedConsumerOrganizationIds([]);
    }
}
